/**
 * brazilianPhonetics.ts
 * 
 * Brazilian Portuguese phonetics and pronunciation guide for English learners.
 * Generates intuitive Brazilian-friendly phonetic respellings ("Como falar em português"),
 * detects tricky phonemes, explains mouth/tongue positions, and warns about common
 * Brazilian pronunciation traps (silent letters, epenthetic "i", final "-m", "-ed" rules).
 */

export interface PhonemeInfo {
  symbol: string;
  name: string;
  mouthTip: string;
  portugueseExplanation: string;
  examples: string[];
}

export interface BrazilianPhoneticGuide {
  word: string;
  ipa: string;
  respelling: string;
  stressedSyllable?: string;
  syllables: string[];
  keyPhonemes: PhonemeInfo[];
  traps: string[];
  generalTip?: string;
}

// Master phoneme registry with clear Portuguese instructions
export const PHONEME_REGISTRY: Record<string, PhonemeInfo> = {
  '/θ/': {
    symbol: '/θ/',
    name: 'TH Surdo (sem voz)',
    mouthTip: 'Ponta da língua entre os dentes superiores',
    portugueseExplanation: 'Posicione a ponta da língua entre os dentes da frente e solte o ar suavemente. Não vibre as cordas vocais! Não pronuncie como "F" nem como "S".',
    examples: ['think', 'three', 'both', 'thought']
  },
  '/ð/': {
    symbol: '/ð/',
    name: 'TH Sonoro (com voz)',
    mouthTip: 'Língua entre os dentes com vibração',
    portugueseExplanation: 'Língua entre os dentes, mas vibrando as cordas vocais — parece um "Z" com a língua presa. Nunca fale como "D".',
    examples: ['this', 'that', 'they', 'together']
  },
  '/æ/': {
    symbol: '/æ/',
    name: 'A Aberto (entre A e É)',
    mouthTip: 'Boca aberta na posição de A, emitindo som de É',
    portugueseExplanation: 'Abra a boca para falar "A", mas tente pronunciar "É". É um som aberto e plano, típico do inglês americano.',
    examples: ['cat', 'apple', 'black', 'family']
  },
  '/ʌ/': {
    symbol: '/ʌ/',
    name: 'U/A Curto (som de Â seco)',
    mouthTip: 'Boca relaxada, som gutural curto',
    portugueseExplanation: 'Som de "Â" curto e seco saído da garganta. Não faça bico nem pronuncie como "U" do português.',
    examples: ['cup', 'love', 'bus', 'money', 'come']
  },
  '/ɪ/': {
    symbol: '/ɪ/',
    name: 'I Curto / Relaxado (som entre E e I)',
    mouthTip: 'Boca entreaberta e lábios soltos',
    portugueseExplanation: 'Um "I" preguiçoso e rápido, mais próximo do nosso "E" fechado de "você". Se você falar o "I" agudo do português, parecerá outra palavra (ex: sit vs seat, bitch vs beach).',
    examples: ['sit', 'ship', 'hit', 'fill', 'live']
  },
  '/iː/': {
    symbol: '/iː/',
    name: 'I Longo / Sorridente',
    mouthTip: 'Lábios bem abertos em sorriso, som longo',
    portugueseExplanation: 'Puxe os cantos da boca em sorriso e sustente o som de "I" tenso por um instante a mais.',
    examples: ['see', 'seat', 'sheep', 'feel', 'leave']
  },
  '/ŋ/': {
    symbol: '/ŋ/',
    name: 'NG Velar (nasal no fundo da garganta)',
    mouthTip: 'Fundo da língua encosta no palato mole',
    portugueseExplanation: 'Som nasal produzido no fundo da garganta. O "G" final NÃO deve ser pronunciado como "gui" nem falado alto!',
    examples: ['sing', 'morning', 'doing', 'ring']
  },
  '/dʒ/': {
    symbol: '/dʒ/',
    name: 'J / DG Africado',
    mouthTip: 'Ponta da língua no céu da boca soltando com explosão',
    portugueseExplanation: 'Como o "dia" falado no Rio de Janeiro ou São Paulo capital, ou o som de "dj" em "jeans".',
    examples: ['job', 'bridge', 'schedule', 'general']
  },
  '/tʃ/': {
    symbol: '/tʃ/',
    name: 'CH Africado',
    mouthTip: 'Explosão de ar com som de TCH',
    portugueseExplanation: 'Exatamente como o som de "tchau" ou "tchê". Não pronuncie como o nosso "CH" suave de "chuva".',
    examples: ['chair', 'teach', 'chocolate', 'church']
  },
  '/ʃ/': {
    symbol: '/ʃ/',
    name: 'SH Fricativo',
    mouthTip: 'Lábios arredondados pedindo silêncio ("shhh")',
    portugueseExplanation: 'Como o "x" de "xícara" ou "shhh" para pedir silêncio.',
    examples: ['she', 'shoe', 'fashion', 'nation']
  },
  '/ɜːr/': {
    symbol: '/ɜːr/',
    name: 'R Retroflexo com Schwa (som de "UR")',
    mouthTip: 'Língua encolhida e curvada para trás sem tocar o céu da boca',
    portugueseExplanation: 'O famoso R do interior de São Paulo (porta, verde). Não pronuncie com som de "R" aspirado (como "rato").',
    examples: ['world', 'girl', 'work', 'nurse', 'turn']
  },
  '/əl/': {
    symbol: '/əl/',
    name: 'L Escuro / Dark L',
    mouthTip: 'Ponta da língua sobe e toca atrás dos dentes superiores',
    portugueseExplanation: 'No português brasileiro nós trocamos o L final por U (Brasil = "Brasiu"). No inglês, mantenha a ponta da língua colada no céu da boca ao terminar!',
    examples: ['apple', 'table', 'people', 'hotel', 'little']
  },
  '/ə/': {
    symbol: '/ə/',
    name: 'Schwa — Vogal Relaxada (som de "Â" bem curto e fraco)',
    mouthTip: 'Boca totalmente relaxada e entreaberta, som curtíssimo',
    portugueseExplanation: 'É o som mais comum do inglês! Soa como o nosso "A" final bem fraco de "cama" ou "porta" (um "â" preguiçoso e rápido). No final de palavras com "-er" (como spanner, water), no inglês britânico soa apenas "â" suave, e no americano ganha um R enrolado ("âr").',
    examples: ['spanner', 'about', 'banana', 'teacher', 'support']
  },
  '/ɒ/': {
    symbol: '/ɒ/',
    name: 'O Aberto Curto (entre "Ó" e "Á")',
    mouthTip: 'Queixo bem baixo (posição de A) com leve arredondamento de Ó',
    portugueseExplanation: 'Som de "Ó" bem aberto e curto (como em "avó" ou "pote"). No inglês britânico soa como um "Ó" aberto seco; no inglês americano o queixo desce mais e soa quase como um "Á" aberto (por isso "hot" e "stop" soam próximos de "rát" e "stáp").',
    examples: ['myopic', 'hot', 'stop', 'lot', 'doctor', 'box']
  },
  '/ɔː/': {
    symbol: '/ɔː/',
    name: 'O Longo Arredondado (som de "Ó" prolongado)',
    mouthTip: 'Lábios arredondados projetados à frente, som longo',
    portugueseExplanation: 'Um som de "Ó" aberto mais longo e encorpado, mantendo os lábios arredondados por um instante a mais.',
    examples: ['water', 'thought', 'talk', 'walk', 'law', 'call']
  },
  '/aɪ/': {
    symbol: '/aɪ/',
    name: 'Ditongo ÁI (som aberto de "AI")',
    mouthTip: 'Começa com a boca bem aberta no A e fecha suavemente no I',
    portugueseExplanation: 'Mesmo quando escrito com as letras "Y" ou "I" (como em myopic, vital, time), pronuncia-se como o nosso "AI" de "pai" ou "vai", e nunca como "I" puro do português.',
    examples: ['myopic', 'time', 'like', 'night', 'sky', 'idea']
  },
  '/ʊ/': {
    symbol: '/ʊ/',
    name: 'U Curto / Relaxado (som entre "Ô" e "U")',
    mouthTip: 'Lábios relaxados sem fazer bico forte',
    portugueseExplanation: 'Um "U" curto e frouxo, quase um meio-termo entre "Ô" e "U" (como o final de "mau"). Não faça o bico tenso do nosso "U" de "tudo".',
    examples: ['book', 'good', 'look', 'put', 'should', 'could']
  },
  '/ɹ/': {
    symbol: '/ɹ/',
    name: 'R Inglês Retroflexo (língua curvada, nunca som de "RR")',
    mouthTip: 'Ponta da língua curvada para trás sem encostar no céu da boca',
    portugueseExplanation: 'No inglês, o "R" (no início da palavra ou em encontros como "pr", "tr", "cr") NUNCA tem som de "RR" de "rato" nem treme a língua como em "prato". Enrole a língua levemente para trás (como o R caipira) antes de soltar a vogal.',
    examples: ['reciprocate', 'red', 'right', 'try', 'problem']
  }
};

// Handcrafted accurate pronunciation guides for common high-frequency tricky words
const TRICKY_WORDS_DATABASE: Record<string, {
  respelling: string;
  ipa: string;
  syllables: string[];
  traps: string[];
  phonemes: string[];
  tip?: string;
}> = {
  'schedule': {
    respelling: 'SKÉ-djiu',
    ipa: '/ˈskedʒ.uːl/',
    syllables: ['ske', 'dule'],
    phonemes: ['/dʒ/'],
    traps: ['No inglês americano, pronuncia-se "SKÉ-djiu" (som de dj). No britânico soa "CHÉ-diul".'],
    tip: 'A ênfase é total na primeira sílaba: SKÉ.'
  },
  'comfortable': {
    respelling: 'CÂMF-târ-bou',
    ipa: '/ˈkʌmftəbl/',
    syllables: ['comf', 'ter', 'ble'],
    phonemes: ['/ʌ/', '/ə/', '/əl/'],
    traps: ['O "or" do meio é TOTALMENTE MUDO! Nunca fale "com-for-te-ibou".', 'São apenas 3 sílabas reais: COMF-ter-ble.'],
    tip: 'Fale rápido: CÂMF-târ-bou.'
  },
  'world': {
    respelling: 'UÔRLD',
    ipa: '/wɜːrld/',
    syllables: ['world'],
    phonemes: ['/ɜːr/', '/əl/'],
    traps: ['Cuidado para não engolir o "L" e falar "word" (palavra)!', 'Enrole a língua no R e encoste a ponta no céu da boca para o L.'],
    tip: 'Combine: "Uôr" + "L" + "D". Trave no D sem colocar "i" no final.'
  },
  'thought': {
    respelling: 'TÓT',
    ipa: '/θɔːt/',
    syllables: ['thought'],
    phonemes: ['/θ/'],
    traps: ['O "ough" soa como "Ó" aberto.', 'O "th" é soprado com a língua nos dentes. Nunca fale "fót" nem "sót".'],
    tip: 'Língua nos dentes e som de Ó: TÓT.'
  },
  'through': {
    respelling: 'THRUU',
    ipa: '/θruː/',
    syllables: ['through'],
    phonemes: ['/θ/'],
    traps: ['O "gh" é mudo.', 'Língua entre os dentes + R enrolado + som longo de U.'],
    tip: 'Rima com "blue" e "true".'
  },
  'receipt': {
    respelling: 'ri-SIIT',
    ipa: '/rɪˈsiːt/',
    syllables: ['re', 'ceipt'],
    phonemes: ['/ɪ/', '/iː/'],
    traps: ['O "P" é 100% MUDO! Nunca fale "re-cép-ti".'],
    tip: 'Sílaba forte no final: ri-SIIT.'
  },
  'island': {
    respelling: 'ÁI-lênd',
    ipa: '/ˈaɪ.lənd/',
    syllables: ['is', 'land'],
    phonemes: ['/əl/'],
    traps: ['O "S" é completamente MUDO! Nunca fale "is-land" nem "áis-land".'],
    tip: 'Diga "Ái" + "lênd".'
  },
  'salmon': {
    respelling: 'SÉ-mân',
    ipa: '/ˈsæm.ən/',
    syllables: ['sal', 'mon'],
    phonemes: ['/æ/', '/ə/'],
    traps: ['O "L" é completamente MUDO! Nunca diga "sál-mon".'],
    tip: 'Fale como "SÉ-mân".'
  },
  'subtle': {
    respelling: 'SÂ-tou',
    ipa: '/ˈsʌt.əl/',
    syllables: ['sub', 'tle'],
    phonemes: ['/ʌ/', '/əl/'],
    traps: ['O "B" é completamente MUDO! Diga "sâ-tou", não "sub-til".'],
    tip: 'Apenas duas sílabas com B mudo: SÂ-tou.'
  },
  'doubt': {
    respelling: 'DÁUT',
    ipa: '/daʊt/',
    syllables: ['doubt'],
    phonemes: [],
    traps: ['O "B" é completamente MUDO! Rima com "out" e "shout".', 'Não coloque "i" no final (nada de "dáuti").'],
    tip: 'Diga "DÁUT" com parada seca no T.'
  },
  'debt': {
    respelling: 'DÉT',
    ipa: '/det/',
    syllables: ['debt'],
    phonemes: [],
    traps: ['O "B" é mudo! Rima com "get" e "bet".'],
    tip: 'DÉT seco.'
  },
  'clothes': {
    respelling: 'CLÔUDZ',
    ipa: '/kloʊðz/',
    syllables: ['clothes'],
    phonemes: ['/ð/'],
    traps: ['Nunca pronuncie "clô-tíz"! É uma sílaba só.', 'Rima quase perfeitamente com o verbo "closes".'],
    tip: 'Fale CLÔUDZ em uma única respiração.'
  },
  'asked': {
    respelling: 'ÉSkt',
    ipa: '/æskt/',
    syllables: ['asked'],
    phonemes: ['/æ/'],
    traps: ['O "-ed" soa como "T". NUNCA fale "ás-qued" ou "ás-quedi".', 'A sequência S-K-T é feita sem nenhuma vogal entre elas.'],
    tip: 'Uma única sílaba: ÉSK + T.'
  },
  'vegetable': {
    respelling: 'VÉDJ-tâ-bou',
    ipa: '/ˈvedʒ.tə.bəl/',
    syllables: ['veg', 'ta', 'ble'],
    phonemes: ['/dʒ/', '/ə/', '/əl/'],
    traps: ['São apenas 3 sílabas! O segundo "e" é mudo. Nunca fale "ve-ge-tá-bel".'],
    tip: 'VÉDJ + tâ + bou.'
  },
  'chocolate': {
    respelling: 'TCHÓ-clât',
    ipa: '/ˈtʃɒk.lət/',
    syllables: ['choc', 'late'],
    phonemes: ['/tʃ/', '/ə/'],
    traps: ['São apenas 2 sílabas! O "o" do meio é omitido na fala natural.', 'O final soa "clât", não "lêite".'],
    tip: 'TCHÓ-clât.'
  },
  'wednesday': {
    respelling: 'UÉNZ-dei',
    ipa: '/ˈwenz.deɪ/',
    syllables: ['wednes', 'day'],
    phonemes: [],
    traps: ['O primeiro "d" e o "e" são mudos! NUNCA diga "uéd-nes-dei".'],
    tip: 'Diga simplesmente UÉNZ-dei (duas sílabas).'
  },
  'february': {
    respelling: 'FÉB-ru-e-ri',
    ipa: '/ˈfeb.ru.er.i/',
    syllables: ['feb', 'ru', 'ar', 'y'],
    phonemes: [],
    traps: ['No inglês americano é muito comum pronunciar "FÉB-yu-e-ri", omitindo o primeiro R.'],
    tip: 'FÉB-ru-e-ri ou FÉB-yu-e-ri.'
  },
  'hotel': {
    respelling: 'rou-TÉL',
    ipa: '/hoʊˈtel/',
    syllables: ['ho', 'tel'],
    phonemes: [],
    traps: ['No português dizemos HO-tel com ênfase no HO. No inglês a sílaba forte é no TÉL!'],
    tip: 'Ênfase no final: rou-TÉL.'
  },
  'development': {
    respelling: 'di-VÉ-lâp-mênt',
    ipa: '/dɪˈvel.əp.mənt/',
    syllables: ['de', 'vel', 'op', 'ment'],
    phonemes: ['/ɪ/', '/ə/'],
    traps: ['A sílaba forte é no VÉL! Brasileiros costumam falar "déve-lop-ment", o que soa errado.'],
    tip: 'di-VÉ-lâp-mênt.'
  },
  'determine': {
    respelling: 'di-TÊR-mân',
    ipa: '/dɪˈtɜːr.mɪn/',
    syllables: ['de', 'ter', 'mine'],
    phonemes: ['/ɜːr/'],
    traps: ['O final soa "-mân" ou "-min", NUNCA "-máin"!'],
    tip: 'di-TÊR-mân.'
  },
  'beach': {
    respelling: 'BIITCH',
    ipa: '/biːtʃ/',
    syllables: ['beach'],
    phonemes: ['/iː/', '/tʃ/'],
    traps: ['O som de "ea" é um I longo e sorridente. Se você falar o "I" curto e relaxado, soará como o xingamento "bitch"!'],
    tip: 'Abra um sorriso e prolongue o "I": BIITCH.'
  },
  'bitch': {
    respelling: 'BÉTCH / BITCH (curto)',
    ipa: '/bɪtʃ/',
    syllables: ['bitch'],
    phonemes: ['/ɪ/', '/tʃ/'],
    traps: ['Som de I curto e frouxo, quase um "É" fechado. Diferente de "beach" que é longo.'],
    tip: 'Som seco e curto.'
  },
  'sheet': {
    respelling: 'SHIIT',
    ipa: '/ʃiːt/',
    syllables: ['sheet'],
    phonemes: ['/iː/', '/ʃ/'],
    traps: ['Prolongue o "I" para não confundir com o palavrão "shit" (que tem o I curto).'],
    tip: 'Som longo de I: SHIIT.'
  },
  'shit': {
    respelling: 'SHÉT / SHIT (curto)',
    ipa: '/ʃɪt/',
    syllables: ['shit'],
    phonemes: ['/ɪ/', '/ʃ/'],
    traps: ['I curto e relaxado, seco.'],
    tip: 'Curto e seco.'
  },
  'listen': {
    respelling: 'LÍ-sân',
    ipa: '/ˈlɪs.ən/',
    syllables: ['lis', 'ten'],
    phonemes: ['/ɪ/', '/ə/'],
    traps: ['O "T" é completamente MUDO! NUNCA diga "lis-ten".'],
    tip: 'LÍ-sân com T mudo.'
  },
  'castle': {
    respelling: 'KÉ-sou',
    ipa: '/ˈkæs.əl/',
    syllables: ['cas', 'tle'],
    phonemes: ['/æ/', '/əl/'],
    traps: ['O "T" é mudo! Diga KÉ-sou, não "cas-tel".'],
    tip: 'KÉ-sou.'
  },
  'knife': {
    respelling: 'NÁIF',
    ipa: '/naɪf/',
    syllables: ['knife'],
    phonemes: [],
    traps: ['O "K" inicial é mudo em palavras que começam com "kn-" (knife, knee, know, knight).'],
    tip: 'NÁIF.'
  },
  'knee': {
    respelling: 'NII',
    ipa: '/niː/',
    syllables: ['knee'],
    phonemes: ['/iː/'],
    traps: ['O "K" inicial é mudo!'],
    tip: 'NII.'
  },
  'knight': {
    respelling: 'NÁIT',
    ipa: '/naɪt/',
    syllables: ['knight'],
    phonemes: [],
    traps: ['Tanto o "K" quanto o "gh" são completamente mudos! Soa igual a "night".'],
    tip: 'NÁIT.'
  },
  'people': {
    respelling: 'PII-pou',
    ipa: '/ˈpiː.pəl/',
    syllables: ['peo', 'ple'],
    phonemes: ['/iː/', '/əl/'],
    traps: ['O "eo" soa simplesmente como "I" longo. O final tem som de "-pou", não de "-pôu-le".'],
    tip: 'PII-pou.'
  },
  'apple': {
    respelling: 'É-pou',
    ipa: '/ˈæp.əl/',
    syllables: ['ap', 'ple'],
    phonemes: ['/æ/', '/əl/'],
    traps: ['O "A" inicial é aberto (/æ/). O "-ple" soa como "-pou" com o L tocando no céu da boca.'],
    tip: 'É-pou.'
  },
  'focus': {
    respelling: 'FÔU-câs',
    ipa: '/ˈfoʊ.kəs/',
    syllables: ['fo', 'cus'],
    phonemes: ['/ə/'],
    traps: ['O "O" inicial tem som de "ÔU" (ditongo). Cuidado para não falar "fócus" como no português.'],
    tip: 'FÔU-câs.'
  },
  'culture': {
    respelling: 'CÂL-tchâr',
    ipa: '/ˈkʌl.tʃər/',
    syllables: ['cul', 'ture'],
    phonemes: ['/ʌ/', '/tʃ/', '/ə/'],
    traps: ['A terminação "-ture" soa como "TCHÂR", nunca como "-tu-ra" ou "-tur".'],
    tip: 'CÂL-tchâr.'
  },
  'nature': {
    respelling: 'NÊI-tchâr',
    ipa: '/ˈneɪ.tʃər/',
    syllables: ['na', 'ture'],
    phonemes: ['/tʃ/', '/ə/'],
    traps: ['O "A" soa como "ÊI" e o "-ture" soa como "TCHÂR".'],
    tip: 'NÊI-tchâr.'
  },
  'future': {
    respelling: 'FIU-tchâr',
    ipa: '/ˈfjuː.tʃər/',
    syllables: ['fu', 'ture'],
    phonemes: ['/tʃ/', '/ə/'],
    traps: ['O "-ture" soa como "TCHÂR".'],
    tip: 'FIU-tchâr.'
  },
  'picture': {
    respelling: 'PÍK-tchâr',
    ipa: '/ˈpɪk.tʃər/',
    syllables: ['pic', 'ture'],
    phonemes: ['/ɪ/', '/tʃ/', '/ə/'],
    traps: ['O "-ture" soa como "TCHÂR". Trave no K sem adicionar som de "i" (nada de "piki-tcher").'],
    tip: 'PÍK-tchâr.'
  }
};

/**
 * Common Brazilian pronunciation traps analyzer based on word morphology and orthography.
 */
export function getBrazilianPronunciationTraps(word: string): string[] {
  const traps: string[] = [];
  const clean = word.toLowerCase().trim();

  // 1. Silent letter detection
  if (/^kn/i.test(clean)) {
    traps.push('Letra muda: O "K" inicial antes de N não é pronunciado (fale apenas o som do N).');
  }
  if (/^ps|^pn|^pt/i.test(clean)) {
    traps.push('Letra muda: O "P" inicial é silencioso antes de S, N ou T.');
  }
  if (/^wr/i.test(clean)) {
    traps.push('Letra muda: O "W" inicial antes de R não é pronunciado (ex: write, wrong).');
  }
  if (/mb$/i.test(clean) || /bt$/i.test(clean)) {
    traps.push('Letra muda: O "B" final após M ou antes de T é silencioso (ex: climb, bomb, doubt, debt).');
  }
  if (/mn$/i.test(clean)) {
    traps.push('Letra muda: O "N" final após M é mudo (ex: autumn, column).');
  }
  if (/(stle|sten)$/i.test(clean)) {
    traps.push('Letra muda: O "T" é mudo nas terminações "-stle" e "-sten" (ex: castle, listen, whistle).');
  }
  if (/alk$/i.test(clean) || /alf$/i.test(clean) || /alm$/i.test(clean)) {
    traps.push('Letra muda: O "L" não se pronuncia em palavras como talk, walk, half, calm, salmon.');
  }

  // 2. Paragoge / Epenthesis warning: adding "i" at consonant ends
  if (/[bcdfghkpt]$/i.test(clean) && !/(ch|sh|th)$/i.test(clean)) {
    const rawLastChar = clean.slice(-1).toUpperCase();
    // Words ending in -c (like myopic, music, basic) have a /k/ sound, not /s/
    const phoneticLastChar = rawLastChar === 'C' ? 'K' : rawLastChar;
    traps.push(`Cuidado com o vício brasileiro de colocar "i" no final! Trave a língua/lábios no som de "${phoneticLastChar}", nunca fale com som de "-${phoneticLastChar.toLowerCase()}i" (ex: stop, like, cat, music).`);
  }

  // 3. Final -m vs -n
  if (/m$/i.test(clean)) {
    traps.push('Feche os lábios no "M" final! No português costumamos apenas anasalhar a vogal (como em "bom"). No inglês é obrigatório fechar completamente a boca ao terminar (ex: from, team, him).');
  }

  // 4. Past tense -ed rule
  if (/ed$/i.test(clean) && clean.length > 3) {
    const root = clean.slice(0, -2);
    if (/t$/i.test(root) || /d$/i.test(root) || /te$/i.test(root) || /de$/i.test(root)) {
      traps.push('Regra do -ED: Como a palavra termina em som de T ou D, o "-ed" é pronunciado como uma nova sílaba: "/ɪd/" (ex: wanted, needed).');
    } else if (/[pkfsʃθ]|ch|sh$/i.test(root)) {
      traps.push('Regra do -ED: Como a palavra termina em som surdo (sem voz), o "-ed" soa seco como um "/T/". NUNCA fale "-edi" (ex: asked = "éskt", watched = "uótcht").');
    } else {
      traps.push('Regra do -ED: O "-ed" soa suave como "/D/". Não adicione uma vogal "i" (ex: played = "plêid", called = "côuld").');
    }
  }

  // 5. "-ture", "-tion", or unstressed "-er/-or/-ar" endings
  if (/ture$/i.test(clean)) {
    traps.push('Terminação "-ture": Pronuncia-se com som de "TCHÂR" (ex: future = "FIU-tchâr", culture = "CÂL-tchâr").');
  } else if (/(er|or|ar)$/i.test(clean) && clean.length > 4) {
    traps.push('Terminação "-er/-or": A última sílaba é bem fraca e relaxada (som de "-âr" com R caipira no inglês americano, ou apenas "-â" suave sem R no inglês britânico).');
  }
  if (/tion$|sion$/i.test(clean)) {
    traps.push('Terminação "-tion": Pronuncia-se com som de "SHÂN" (como em "nation" = "NÊI-shân").');
  }

  return traps;
}

/**
 * Detect which tricky English phonemes exist in a given word or its IPA notation.
 */
export function detectTrickyPhonemes(word: string, ipaText?: string): PhonemeInfo[] {
  const result: PhonemeInfo[] = [];
  const cleanWord = word.toLowerCase();
  const cleanIpa = (ipaText || '').toLowerCase();
  // True when ipaText is an actual phonetic transcription rather than a fallback `/${cleanWord}/`
  const hasRealIpa = cleanIpa.length > 0 && cleanIpa.replace(/^\/+|\/+$/g, '') !== cleanWord;

  const addIfMissing = (key: string) => {
    const info = PHONEME_REGISTRY[key];
    if (info && !result.some(p => p.symbol === info.symbol)) {
      result.push(info);
    }
  };

  // 1. TH sounds
  if (cleanIpa.includes('θ') || (!hasRealIpa && /\bth|th\b|thought|think|three|both|health|south/i.test(cleanWord))) {
    addIfMissing('/θ/');
  }
  if (cleanIpa.includes('ð') || (!hasRealIpa && /\b(this|that|they|them|there|other|mother|weather|clothe)\b/i.test(cleanWord))) {
    addIfMissing('/ð/');
  }

  // 2. Open A sound /æ/
  if (cleanIpa.includes('æ') || (!hasRealIpa && /\b(cat|black|apple|bad|family|man|back|plan|map|happy|ask|fast)\b/i.test(cleanWord))) {
    addIfMissing('/æ/');
  }

  // 3. Short gutural U /ʌ/
  if (cleanIpa.includes('ʌ') || (!hasRealIpa && /\b(cup|love|bus|money|come|done|some|sun|fun|run|study|blood)\b/i.test(cleanWord))) {
    addIfMissing('/ʌ/');
  }

  // 4. Short lax I /ɪ/ vs Long I /iː/
  if (cleanIpa.includes('iː') || (!hasRealIpa && (/ee|ea|ie/i.test(cleanWord) || /\b(see|seat|beach|sheep|feel|leave|team)\b/i.test(cleanWord)))) {
    addIfMissing('/iː/');
  }
  if (cleanIpa.includes('ɪ') || (!hasRealIpa && /\b(sit|ship|hit|bitch|fill|live|will|with|milk|pig|rich|trip)\b/i.test(cleanWord))) {
    addIfMissing('/ɪ/');
  }

  // 5. NG sound
  if (cleanIpa.includes('ŋ') || /ing$|ong$|ung$|ang$|\b(sing|song|ring|hang)\b/i.test(cleanWord)) {
    addIfMissing('/ŋ/');
  }

  // 6. J / DG / CH
  if (cleanIpa.includes('dʒ') || (!hasRealIpa && (/ge$|dge$|\b(job|bridge|schedule|major|gym|jump|general)\b/i.test(cleanWord)))) {
    addIfMissing('/dʒ/');
  }
  if (cleanIpa.includes('tʃ') || (!hasRealIpa && (/ch|tch|\b(nature|future|picture|church|chair|teach)\b/i.test(cleanWord)))) {
    addIfMissing('/tʃ/');
  }

  // 7. Dark L
  if (cleanIpa.includes('əl') || cleanIpa.includes('ɫ') || /ple$|ble$|tle$|cle$|dle$|gle$|\b(world|hotel|apple|table|people)\b/i.test(cleanWord)) {
    addIfMissing('/əl/');
  }

  // 8. Retroflex R (/ɜːr/ and /ɹ/)
  if (cleanIpa.includes('ɜːr') || cleanIpa.includes('ɜː') || cleanIpa.includes('ər') || (!hasRealIpa && /\b(world|girl|work|turn|learn|nurse|bird|word)\b/i.test(cleanWord))) {
    addIfMissing('/ɜːr/');
  }
  if (cleanIpa.includes('ɹ') || /^r|[pbtdkgf]r/i.test(cleanWord)) {
    addIfMissing('/ɹ/');
  }

  // 9. Schwa /ə/ (relaxed neutral vowel)
  const ipaWithoutDiphthongs = cleanIpa.replace(/əʊ|əl|ɜːr|ɪə|eə|ʊə/g, '');
  if (
    ipaWithoutDiphthongs.includes('ə') ||
    (/(er|or|ar|tion|sion|ment|able|ous)$/i.test(cleanWord) && cleanWord.length > 4)
  ) {
    addIfMissing('/ə/');
  }

  // 10. Open short O /ɒ/ & Long O /ɔː/
  if (cleanIpa.includes('ɒ') || cleanIpa.includes('Ɒ')) {
    addIfMissing('/ɒ/');
  }
  if (cleanIpa.includes('ɔː')) {
    addIfMissing('/ɔː/');
  }

  // 11. Diphthong /aɪ/
  if (cleanIpa.includes('aɪ')) {
    addIfMissing('/aɪ/');
  }

  // 12. Short lax U /ʊ/
  const ipaWithoutAuOu = cleanIpa.replace(/aʊ|oʊ|əʊ|ʊə/g, '');
  if (ipaWithoutAuOu.includes('ʊ') || /book|good|look|took|foot|wood|should|could|would|put|push|pull/i.test(cleanWord)) {
    addIfMissing('/ʊ/');
  }

  return result;
}

/**
 * Splits an IPA string into phonetic syllables even when the dictionary API
 * omits dot separators (e.g. "/maɪˈɒpɪk/" -> ["maɪ", "ˈɒ", "pɪk"]).
 */
function splitIpaIntoSyllables(rawIpa: string): string[] {
  // 1. Split on explicit syllable dots or right before primary/secondary stress marks
  const initialChunks = rawIpa
    .replace(/\.+/g, '.')
    .replace(/([ˈˌ])/g, '.$1')
    .split('.')
    .map(s => s.trim())
    .filter(Boolean);

  // Regex matching single IPA vowel nuclei (diphthongs/long vowels first, then monophthongs)
  const vowelTokenRegex = /(eə\(r\)|ɪə\(r\)|ʊə\(r\)|eə|ɪə|ʊə|aɪ|eɪ|ɔɪ|aʊ|oʊ|əʊ|ɜːr|ɜː|iː|uː|ɔː|ɑː|[æʌəɪʊɛɒⱰiuaeo])/g;

  const finalSyllables: string[] = [];

  for (const chunk of initialChunks) {
    const matches = Array.from(chunk.matchAll(vowelTokenRegex));
    if (matches.length <= 1) {
      finalSyllables.push(chunk);
      continue;
    }

    // Split multi-vowel chunk into syllables: place boundary before the last consonant preceding the next vowel
    let startIdx = 0;
    for (let i = 0; i < matches.length - 1; i++) {
      const currentVowelEnd = (matches[i].index || 0) + matches[i][0].length;
      const nextVowelStart = matches[i + 1].index || 0;
      const intervocalic = chunk.slice(currentVowelEnd, nextVowelStart);

      let splitPos = currentVowelEnd;
      if (intervocalic.length === 1) {
        // V + C + V -> V - CV (e.g. "ˈɒpɪk" -> "ˈɒ" + "pɪk")
        splitPos = currentVowelEnd;
      } else if (intervocalic.length > 1) {
        // V + CC + V -> keep affricates/clusters together (including IPA ɹ and ɡ) or split before last consonant
        if (/(dʒ|tʃ|[tdpbkgɡf][rɹ]|[pbkgɡf]l|sp|st|sk)$/.test(intervocalic)) {
          splitPos = nextVowelStart - 2;
        } else {
          splitPos = nextVowelStart - 1;
        }
      }

      finalSyllables.push(chunk.slice(startIdx, splitPos));
      startIdx = splitPos;
    }
    finalSyllables.push(chunk.slice(startIdx));
  }

  return finalSyllables.filter(Boolean);
}

/**
 * Algorithmic generator for Brazilian phonetic respelling when word is not in database.
 * Maps English orthography and IPA patterns to accessible Portuguese syllables.
 */
export function generateBrazilianRespelling(word: string, ipaText?: string): string {
  const cleanWord = word.trim().toLowerCase();
  
  // 1. Check curated database first
  if (TRICKY_WORDS_DATABASE[cleanWord]) {
    return TRICKY_WORDS_DATABASE[cleanWord].respelling;
  }

  // 2. If valid IPA is provided, perform IPA-to-Portuguese phonetic conversion
  if (ipaText && ipaText.length > 1) {
    const rawIpa = ipaText.replace(/^\/+|\/+$/g, '').trim();
    const rawSyllables = splitIpaIntoSyllables(rawIpa);
    const hasExplicitStress = rawIpa.includes('ˈ') || rawSyllables.length > 1;
    const endsWithOrthographicR = /r$/i.test(cleanWord);

    const convertSyllable = (syl: string, isLast: boolean, isStressed: boolean): string => {
      let s = syl
        .replace(/[ˈˌ/]/g, '')
        // Centering diphthongs (British/American r-colored vowels) before standalone schwa
        .replace(/eə\(?r?\)?|ɛə\(?r?\)?/g, 'ér')
        .replace(/ɪə\(?r?\)?/g, 'ír')
        .replace(/ʊə\(?r?\)?/g, 'úr');

      // If final syllable ends in schwa /ə/ (or /ə(r)/) and English spelling ends in 'r' (e.g., spanner /ˈspæn.ə/),
      // render as 'âr' so Brazilians clearly understand the relaxed final -er sound.
      if (isLast && endsWithOrthographicR) {
        s = s.replace(/ə\(?r?\)?$/g, 'âr');
      }

      return s
        // Consonant digraphs & symbols
        .replace(/dʒ/g, 'dj')
        .replace(/tʃ/g, 'tch')
        .replace(/θ/g, 'th')
        .replace(/ð/g, 'dh')
        .replace(/ʃ/g, 'ch')
        .replace(/ʒ/g, 'j')
        .replace(/ŋ/g, 'ng')
        .replace(/ɡ/g, 'g')
        .replace(/ɹ|ɾ/g, 'r')
        .replace(/ʔ/g, '')
        // Semivowels / glides (/j/ = English 'y' as in yes /jɛs/, /w/ = 'u' as in water /wɔːtə/)
        .replace(/juː|ju/g, 'iu')
        .replace(/j/g, 'i')
        .replace(/w/g, 'u')
        // Rhotic vowels & Dark L
        .replace(/ɜːr|ɜː|ɝ|ər|ɚ/g, 'âr')
        .replace(/əl|ɫ/g, 'ou')
        // Diphthongs (use acute/circumflex accent on stressed syllable, plain on unstressed)
        .replace(/oʊ|əʊ/g, isStressed ? 'ôu' : 'ou')
        .replace(/aɪ/g, isStressed ? 'ái' : 'ai')
        .replace(/eɪ/g, isStressed ? 'êi' : 'ei')
        .replace(/ɔɪ/g, isStressed ? 'ói' : 'oi')
        .replace(/aʊ/g, isStressed ? 'áu' : 'au')
        // Monophthongs
        .replace(/ɔː|ɑː|ɒ|Ɒ/gi, 'ó')
        .replace(/æ|ɛ/g, 'é')
        .replace(/e/g, isStressed ? 'é' : 'e')
        .replace(/ʌ|ə/g, 'â')
        .replace(/iː/g, 'ii')
        .replace(/ɪ/g, 'i')
        .replace(/uː/g, 'uu')
        .replace(/ʊ/g, 'u')
        .replace(/\(?r\)?/g, 'r')
        .replace(/ː/g, '');
    };

    if (rawSyllables.length > 1 && hasExplicitStress) {
      const formattedSyllables = rawSyllables.map((syl, idx) => {
        const isStressed = syl.includes('ˈ') || (idx === 0 && !rawIpa.includes('ˈ'));
        const convertedSyl = convertSyllable(syl, idx === rawSyllables.length - 1, isStressed);
        return isStressed ? convertedSyl.toUpperCase() : convertedSyl.toLowerCase();
      });
      const joined = formattedSyllables.join('-');
      if (joined.length > 1) {
        return joined;
      }
    } else {
      const converted = convertSyllable(rawIpa, true, true);
      if (converted.length > 1) {
        return converted.toUpperCase();
      }
    }
  }

  // 3. Orthographic rule-based conversion for any English word
  let resp = cleanWord;

  // Handle common prefixes and silent letters
  resp = resp.replace(/^kn/g, 'n');
  resp = resp.replace(/^wr/g, 'r');
  resp = resp.replace(/^ps/g, 's');
  resp = resp.replace(/mb$/g, 'm');

  // Handle suffixes
  resp = resp.replace(/tion$/g, '-shân');
  resp = resp.replace(/sion$/g, '-jân');
  resp = resp.replace(/ture$/g, '-tchâr');
  resp = resp.replace(/cious$|tious$/g, '-châs');
  resp = resp.replace(/able$/g, '-â-bou');
  resp = resp.replace(/ible$/g, '-i-bou');
  resp = resp.replace(/ple$/g, '-pou');
  resp = resp.replace(/ble$/g, '-bou');
  resp = resp.replace(/tle$/g, '-tou');
  resp = resp.replace(/dle$/g, '-dou');

  // Common vowel clusters
  resp = resp.replace(/igh/g, 'ái');
  resp = resp.replace(/ee|ea/g, 'ii');
  resp = resp.replace(/oo/g, 'uu');
  resp = resp.replace(/ai|ay/g, 'êi');
  resp = resp.replace(/oi|oy/g, 'ói');
  resp = resp.replace(/ou|ow/g, 'áu');
  resp = resp.replace(/all/g, 'ól');
  resp = resp.replace(/alk/g, 'ók');

  // Consonants
  resp = resp.replace(/tch/g, 'tch');
  resp = resp.replace(/ch/g, 'tch');
  resp = resp.replace(/sh/g, 'sh');
  resp = resp.replace(/th/g, 'th');
  resp = resp.replace(/ph/g, 'f');
  resp = resp.replace(/j/g, 'dj');

  // Clean hyphens and format
  resp = resp.replace(/--+/g, '-').replace(/^-|-$/g, '');
  return resp.toUpperCase();
}

/**
 * Main export: Retrieves full Brazilian pronunciation guidance object for any English word.
 */
export function getBrazilianPhoneticGuide(word: string, apiIpa?: string): BrazilianPhoneticGuide {
  const cleanWord = word.trim().toLowerCase();
  const dbEntry = TRICKY_WORDS_DATABASE[cleanWord];

  const ipa = apiIpa && apiIpa.trim().length > 0 
    ? apiIpa 
    : dbEntry?.ipa || `/${cleanWord}/`;

  const respelling = dbEntry?.respelling || generateBrazilianRespelling(cleanWord, apiIpa);
  
  const syllables = dbEntry?.syllables || (
    cleanWord.length > 6 
      ? respelling.split('-').map(s => s.trim().toLowerCase()) 
      : [cleanWord]
  );

  const keyPhonemes = detectTrickyPhonemes(cleanWord, ipa);
  const traps = [
    ...(dbEntry?.traps || []),
    ...getBrazilianPronunciationTraps(cleanWord)
  ];

  // Deduplicate traps
  const uniqueTraps = Array.from(new Set(traps));

  return {
    word,
    ipa,
    respelling,
    syllables,
    keyPhonemes,
    traps: uniqueTraps,
    generalTip: dbEntry?.tip
  };
}
